import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./Contracts.styles";
import ContractTypeModel from "../../../../models/contract/ContractTypeModel";
import * as ContractAction from "../../../../stores/contracts/ContractAction";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import { consolidateDateToNumber, convertDateStringToNumber, convertNumberToDateString, isDateSameOrAfterDate, today } from "../../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../../constants/TIME";
import { CsvExportButton } from "../../../components/CsvExport/CsvExportButton";

export interface IContractTypesViewProps extends WithStyles<typeof styles> {}

const ContractTypesViewPure: React.FunctionComponent<IContractTypesViewProps> = ({
    classes
}: IContractTypesViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(ContractAction.requestAllContractTypes());
    }, [dispatch]);

    const contractTypesFromState = useSelector(state => state.contracts.types);
    const isRequesting = useSelector(state => selectRequesting(state, [ContractAction.REQUEST_ALL_CONTRACT_TYPES]));

    let contractTypes: IUniversalTableData;
    if (contractTypesFromState) {
        contractTypes = Object.values(contractTypesFromState)?.map((contractType: ContractTypeModel) => ({
            id: contractType.id,
            name: contractType.name,
            validFrom: contractType.validFrom,
            validTo: contractType.validTo
        }));
    }

    const handleEditFinished = (index, newData) => {
        const updatedContractType = {
            validFrom: consolidateDateToNumber(newData.validFrom),
            validTo: consolidateDateToNumber(newData.validTo),
            name: newData.name
        };
        dispatch(ContractAction.updateContractType(contractTypes[index].id, updatedContractType))
            .then(() => {
                dispatch(ContractAction.requestAllContractTypes());
                enqueueSnackbar("Contract type edited", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Contract type editing failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleAddFinished = data => {
        const newContractType = {
            validFrom: consolidateDateToNumber(data.validFrom),
            validTo: consolidateDateToNumber(data.validTo),
            name: data.name
        };
        dispatch(ContractAction.addContractType(newContractType))
            .then(() => {
                dispatch(ContractAction.requestAllContractTypes());
                enqueueSnackbar("Contract type added", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Contract type adding failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            title: "Contract type",
            field: "name",
            type: "multiline_text",
            validate: rowData => {
                return rowData?.name === undefined || rowData?.name?.length === 0
                ? { isValid: false, helperText: "A name must not be empty" }
                : true
            }
        },
        {
            title: "Valid from",
            field: "validFrom",
            type: "date",
            validate: rowData =>
                rowData?.validFrom === 0
                    ? { isValid: false, helperText: "Date must be valid" }
                    : isDateSameOrAfterDate(rowData?.validTo, rowData?.validFrom)
                    ? true
                    : { isValid: false, helperText: "Valid from date must be before valid to" }
        },
        {
            title: "Valid to",
            field: "validTo",
            type: "date",
            defaultValue: convertDateStringToNumber(today().add(1, "years").format(DATE_FORMAT)),
            validate: rowData =>
                rowData?.validTo === 0
                    ? { isValid: false, helperText: "Date must be valid" }
                    : isDateSameOrAfterDate(rowData?.validTo, rowData?.validFrom)
                    ? true
                    : { isValid: false, helperText: "Valid from date must be before valid to" }
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue),
                    scope: "contracts.write"
                },
                {
                    name: "add",
                    tooltip: "Add a contract type",
                    onAddFinished: newValue => handleAddFinished(newValue),
                    scope: "contracts.write"
                }
            ]
        }
    ];

    const exportMapping = [
        {
            label:  "Contract type",
            key: "name"
        },
        {
            label: "Valid from",
            key: "validFrom",
            mappingFunc: (value) => convertNumberToDateString(value)
        },
        {
            label: "Valid to",
            key: "validTo",
            mappingFunc: (value) => convertNumberToDateString(value)
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: "Contracts", href: "/admin/contracts" },
                    { title: "Manage Contract types" }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">Manage Contract types</Typography>
            </div>
            <div className={classes.exportButton}>
                    <CsvExportButton
                        data={contractTypes || []}
                        outputFilename={`contract_types_${today().format("YYYYMMDD")}.csv`}
                        mappings={exportMapping}
                    >
                        Export contract types
                    </CsvExportButton>
                </div>
            <UniversalTable columns={columns} data={contractTypes} isLoading={isRequesting} searchable />
        </CenteredContainer>
    );
};

export const ContractTypesView = withStyles(styles)(ContractTypesViewPure);
